// libs
import classNames from 'classnames'
import React, { useCallback, useState } from 'react'

// constants
import { CLASS_PREFIX } from 'src/constants'

// styles
import '../project_modal.scss'
import './project_detail.scss'

// icons/colors (same usage as your overview)
import {
  ProjectFeature,
  ProjectFragmentFragment,
  ProjectTopicFragmentFragment,
  useFollowMutation,
  useUnfollowMutation
} from '@lopo-tech/lopo-api'
import { useHistory } from 'react-router-dom'
import { Button } from 'src/components/button/button'
import Icon from 'src/components/forms/icon/icon'
import { LastActivityIcon } from 'src/components/icons/last-activity'
import ProjectMap from 'src/components/map/project_map/project_map'
import { MarkdownRenderer } from 'src/components/markdown_renderer/markdown_renderer'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import { MeetingTile } from 'src/components/projects/meeting/meeting_tile'
import { hasMoreThanShown } from 'src/components/projects/projects_overview/has_more_than_shown'
import { getProjectRoutes } from 'src/constants/urls'
import { textResources } from 'src/lang/de'
import { COLOR_TYPE_PRIMARY, COLOR_TYPE_WHITE } from 'src/utils/color'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { ProjectMilestones } from '../milestones/project_milestones'
import { ProjectCreateMeetingModal } from '../project_create_meeting/project_create_meeting_modal'
import { ProjectCreateMeetingSuccessModal } from '../project_create_meeting/project_create_meeting_success_modal'
import { ProjectParticipant } from '../project_participant/project_participant'
import { DetailCard } from './detail_card/detail_card'
import { ProjectContacts } from './project_contacts/project_contacts'
import { ProjectDetailHero } from './project_detail_hero/project_detail_hero'
import { ProjectDownloadFiles } from './project_download_files/project_download_files'
import ProjectFeatureTabs, { FeatureTab } from './project_feature_tabs/project_feature_tabs'
import { ProjectFeed } from './project_feed/project_feed'
import { SideCard } from './side_card/side_card'

const cls = `${CLASS_PREFIX}project-detail`

const t = textResources.projects

type Props = {
  project: ProjectFragmentFragment;
  topics: ProjectTopicFragmentFragment[];
  getNextPage: () => void;
  loading: boolean;
};

const ProjectDetail: React.FC<Props> = ({ project, topics, getNextPage, loading }) => {
  const [isCreateMeetingOpen, setIsCreateMeetingOpen] = useState(false)
  const [isCreateSuccessOpen, setIsCreateSuccessOpen] = useState(false)
  const history = useHistory()
  const routes = getProjectRoutes(project.type)

  const upcomingMeeting = project.meetings?.items?.[0]

  const isMapShown = !!project.geojson

  // const [isManageOpen, setIsManageOpen] = useState(false)

  const [follow] = useFollowMutation()
  const [unfollow] = useUnfollowMutation()

  const featureTabs: FeatureTab[] = []
  let milestonesTab: FeatureTab | undefined

  for (const feature of project.features) {
    if (feature === ProjectFeature.Milestones) {
      milestonesTab = {
        id: feature,
        label: t.milestones.title,
        content: null
      }
      featureTabs.push(milestonesTab)
    } else if (feature === ProjectFeature.ProjectFeed) {
      featureTabs.push({
        id: feature,
        label: t.featureTabs.projectFeedTitle,
        content: (
          <ProjectFeed topics={topics} getNextPage={getNextPage} loading={loading} />
        )
      })
    } else if (feature === ProjectFeature.Contacts && project.contacts && project.contacts.length > 0) {
      featureTabs.push({
        id: 'contact',
        label: t.featureTabs.contactTitle,
        content: <ProjectContacts contacts={project.contacts} />
      })
    } else if (feature === ProjectFeature.Downloads && project.downloads && project.downloads.length > 0) {
      featureTabs.push({
        id: 'download',
        label: t.featureTabs.downloadTitle,
        content: <ProjectDownloadFiles files={project.downloads} />
      })
    }
  }

  const isSingleTab = featureTabs.length === 1

  if (milestonesTab) {
    const shownMilestones = project.milestones?.items ?? []
    milestonesTab.content = (
      <ProjectMilestones
        projectId={project.id}
        projectSlug={project.slug}
        projectType={project.type}
        milestones={shownMilestones}
        canCreateMilestones={project.canCreateMilestones}
        withPadding={false}
        // The total comes from the same connection as the items, so the two cannot
        // drift apart; `milestonesCount` is a counter cache on the project.
        withShowAllFooter={hasMoreThanShown(
          project.milestones?.pageInfo.totalCount,
          shownMilestones.length
        )}
        withSectionTitle={isSingleTab}
      />
    )
  }

  const toggleFollow = useCallback(async () => {
    if (project.isFollowing) {
      await unfollow({
        variables: {
          input: {
            followeeId: project.id
          }
        }
      })
    } else {
      await follow({
        variables: {
          input: {
            followeeId: project.id
          }
        }
      })
    }
  }, [follow, unfollow, project.isFollowing, project.id])

  return (
    <div className={cls}>
      <div className={`${cls}__wrapper`}>
        <div className={classNames({ [`${cls}__grid`]: !isMapShown })}>
          {/* MAIN COLUMN */}
          <div className={`${cls}__main`}>
            <ProjectDetailHero
              tags={project.tags}
              canShowTags={project.canShowTags}
              constructionStatus={project.constructionStatus}
              slug={project.slug}
              name={project.name}
              imageUrl={project.images[0]?.file.previewUrl}
            />

            {/* ABOUT */}
            <DetailCard>
              <h2 className={`${cls}__section-title`}>
                {t.details.aboutTitle}
              </h2>
              {project.content.__typename === 'MarkdownContent'
                ? (
                  <MarkdownRenderer
                    shortcodes={project.content.shortcodes}
                    text={project.content.text}
                  />
                )
                : (
                  <p className={`${cls}__body`}>{project.content.text}</p>
                )}
            </DetailCard>

            {isMapShown && (
              <div className={`${cls}__map-card`}>
                <DetailCard>
                  <h2 className={`${cls}__section-title`}>{t.details.map}</h2>
                  <div className={`${cls}__map-preview`}>
                    <ProjectMap
                      geojson={project.geojson}
                      boundingBox={project.boundingBox ?? undefined}
                      center={project.boundingBox?.center ?? undefined}
                      interactive={false}
                    />
                  </div>
                  <Button
                    onClick={() => history.push(routes.map(project.slug))}
                    variant='secondary'
                    title={t.details.openMap}
                    iconLeft={<Icon name='map' size={18} color={COLOR_TYPE_PRIMARY} />}
                  />
                </DetailCard>
              </div>
            )}

            <div className={`${cls}__tabs-section`}>
              {isSingleTab
                ? (
                  <DetailCard>
                    {featureTabs[0].content}
                  </DetailCard>
                )
                : (
                  <ProjectFeatureTabs tabs={featureTabs} />
                )}
            </div>

          </div>

          {/* ASIDE - not shown if map is shown. currently map is shown for infrastructure projects */}
          {!isMapShown && (
            <aside className={`${cls}__aside`}>
              <SideCard>
                <div className={`${cls}__info-row`}>
                  <LastActivityIcon
                    className={`${cls}__info-last-activity-icon`}
                  />
                  <div className={`${cls}__info-text`}>
                    <div className={`${cls}__info-strong`}>
                      {DateFormatter.asDayMonthYearLong(project.lastActivityAt)}
                    </div>
                    <div className={`${cls}__info-sub`}>
                      {t.details.sidebar.lastActivity}
                    </div>
                  </div>
                </div>
                {project.followersCount > 0 && (
                  <div className={`${cls}__info-row`}>
                    <Icon
                      name="participants"
                      size={18}
                      color={COLOR_TYPE_PRIMARY}
                    />
                    <div className={`${cls}__info-text`}>
                      <div className={`${cls}__info-strong`}>
                        {t.details.sidebar.followersCountLabel}{' '}
                        {project.followersCount}
                      </div>
                    </div>
                  </div>
                )}
                {(project.canFollow.value || project.canCreateMeetings.value) && (
                  <div className={`${cls}__divider`} />
                )}

                {project.canFollow.value && (
                  <button
                    className={classNames({
                      [`${cls}__secondary-btn`]: project.isFollowing,
                      [`${cls}__primary-btn`]: !project.isFollowing
                    })}
                    type="button"
                    onClick={toggleFollow}
                  >
                    <Icon
                      name={project.isFollowing ? 'check' : 'plus'}
                      size={18}
                      color={
                        project.isFollowing
                          ? COLOR_TYPE_PRIMARY
                          : COLOR_TYPE_WHITE
                      }
                    />
                    <span>
                      {project.isFollowing
                        ? t.details.follow.following
                        : t.details.follow.followProject}
                    </span>
                  </button>
                )}
                {project.canCreateMeetings.value && (
                  <>
                    <button
                      className={classNames(`${cls}__primary-btn`)}
                      type="button"
                      onClick={() => setIsCreateMeetingOpen(true)}
                    >
                      <Icon
                        name={'calendar'}
                        size={18}
                        color={COLOR_TYPE_WHITE}
                      />
                      <span>{t.details.createMeeting}</span>
                    </button>
                  </>
                )}
              </SideCard>

              {project.canShowProjectMemberships.value &&
                !!project.responsibleMemberships?.items.length && (
                <SideCard>
                  <h3 className={`${cls}__side-title`}>
                    {t.details.responsible}
                  </h3>
                  <div className={`${cls}__participants`}>
                    {project.responsibleMemberships.items.map((membership) => {
                      return (
                        <ProjectParticipant
                          key={membership.id}
                          participant={membership.profile}
                        />
                      )
                    })}
                  </div>
                </SideCard>
              )}

              {/* <SideCard>
              <h3 className={`${cls}__side-title`}>{t.details.participants}</h3>
              {participants.map((p) => (
                <div className={`${cls}__person`} key={p.id}>
                  <img
                    className={`${cls}__avatar`}
                    src={p.avatar}
                    alt=""
                    loading="lazy"
                  />
                  <div className={`${cls}__person-name`}>{p.name}</div>
                </div>
              ))}

              {isParticipant && (
                <div className={`${cls}__side-card-button`}>
                  <button
                    className={classNames(`${cls}__secondary-btn`)}
                    type="button"
                    onClick={() => setIsManageOpen(true)}
                  >
                    <Icon
                      name={'participants'}
                      size={18}
                      color={COLOR_TYPE_PRIMARY}
                    />
                    <span>{t.details.manageRequests}</span>
                  </button>
                </div>
              )}
            </SideCard> */}

              {project.canShowMeetings.value && (
                <SideCard>
                  <h3 className={`${cls}__side-title`}>
                    {t.details.upcomingMeeting}
                  </h3>

                  {upcomingMeeting
                    ? (
                      <MeetingTile setMeetingForReport={() => {}} meeting={upcomingMeeting} withPadding={false} />
                    )
                    : (
                      <div className={`${cls}__body`}>{t.details.noUpcomingMeetings}</div>
                    )}

                  <NavigationItem
                    className={`${cls}__meetings-link`}
                    onClick={() => history.push(routes.meetings(project.slug))}
                  >
                    <div className={`${cls}__primary-btn`}>
                      <Icon name="calendar" size={18} color={COLOR_TYPE_WHITE} />
                      <span>{t.details.meetingSchedule}</span>
                    </div>
                  </NavigationItem>
                </SideCard>
              )}
            </aside>
          )}
        </div>
      </div>

      {/* will be added later
      {isRequestSentOpen && (
        <Modal autoSize onClose={() => setIsRequestSentOpen(false)}>
          <div className={`${modalCls}__body`}>
            <div className={`${modalCls}__check`}>
              <Icon name="check" size={22} color={COLOR_TYPE_WHITE} />
            </div>
            <h3 className={`${modalCls}__title`}>
              {t.details.requestSent.title}
            </h3>
            <p className={`${modalCls}__text`}>{t.details.requestSent.text}</p>
            <button
              className={`${modalCls}__primary`}
              type="button"
              onClick={() => {
                setIsRequestSentOpen(false)
                setIsRequestPending(false)
                setIsParticipant(true)
              }}
            >
              {t.details.requestSent.backToProjectPage}
            </button>
          </div>
        </Modal>
      )} */}

      {isCreateMeetingOpen && (
        <ProjectCreateMeetingModal
          projectId={project.id}
          onClose={() => setIsCreateMeetingOpen(false)}
          onCreated={() => {
            setIsCreateMeetingOpen(false)
            setIsCreateSuccessOpen(true)
          }}
        />
      )}

      {isCreateSuccessOpen && (
        <ProjectCreateMeetingSuccessModal
          onClose={() => setIsCreateSuccessOpen(false)}
        />
      )}
      {/* will be added later
      {isManageOpen && (
        <Modal
          autoSize
          onClose={() => setIsManageOpen(false)}
          customClassNames={`${cls}__manage-modal-body`}
        >
          <div className={`${cls}__manage`}>
            <h3 className={`${cls}__manage-title`}>
              {t.details.manageModal.title}
            </h3>
            <p className={`${cls}__manage-sub`}>{t.details.manageModal.sub}</p>

            <div className={`${cls}__manage-list`}>
              {pending.map((r, i) => (
                <div key={r.id} className={`${cls}__req`}>
                  <img
                    className={`${cls}__req-avatar`}
                    src={r.avatar}
                    alt=""
                    loading="lazy"
                  />
                  <div className={`${cls}__req-main`}>
                    <div className={`${cls}__req-name`}>{r.name}</div>
                    <div className={`${cls}__req-time`}>{r.time}</div>
                  </div>

                  {r.status === 'pending'
                    ? (
                      <div className={`${cls}__req-actions`}>
                        <button
                          type="button"
                          className={`${cls}__req-approve`}
                          onClick={() => markRequest(r.id, 'accepted')}
                        >
                          {t.common.accept}
                        </button>
                        <button
                          type="button"
                          className={`${cls}__req-reject`}
                          onClick={() => markRequest(r.id, 'rejected')}
                        >
                          {t.common.reject}
                        </button>
                      </div>
                    )
                    : (
                      <div
                        className={classNames(
                          `${cls}__req-status`,
                          r.status === 'accepted'
                            ? `${cls}__req-status--accepted`
                            : `${cls}__req-status--rejected`
                        )}
                      >
                        <Icon
                          name={r.status === 'accepted' ? 'check' : 'thin-x'}
                          size={12}
                          color={COLOR_TYPE_PRIMARY}
                        />
                        <span>
                          {r.status === 'accepted'
                            ? t.common.accepted
                            : t.common.rejected}
                        </span>
                      </div>
                    )}

                  {i < pending.length - 1 && (
                    <div className={`${cls}__req-sep`} />
                  )}
                </div>
              ))}
            </div>

            {allResolved
              ? (
                <div className={`${cls}__manage-actions`}>
                  <button
                    className={`${cls}__manage-primary`}
                    type="button"
                    onClick={() => setIsManageOpen(false)}
                  >
                    {t.details.manageModal.goBack}
                  </button>
                </div>
              )
              : (
                <div className={`${cls}__manage-actions`}>
                  <button
                    className={`${cls}__manage-primary`}
                    type="button"
                    onClick={acceptAll}
                  >
                    {t.details.manageModal.acceptAll}
                  </button>
                  <button
                    className={`${cls}__manage-outline`}
                    type="button"
                    onClick={rejectAll}
                  >
                    <span>{t.details.manageModal.rejectAll}</span>
                  </button>
                </div>
              )}
          </div>
        </Modal>
      )} */}
    </div>
  )
}

export default ProjectDetail
